import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  HomeIcon,
  ChevronRightIcon,
  PlusIcon,
  DocumentIcon,
  FolderIcon,
  ArrowDownTrayIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  PlayIcon,
  PhotoIcon,
  PresentationChartLineIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';

const MarketingPage = () => {
  const [user, setUser] = useState(null);
  const [materiali, setMateriali] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('tutti');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadData, setUploadData] = useState({
    titolo: '',
    descrizione: '',
    categoria: 'altro',
    tipoVisibilita: 'globale',
    nodoId: '',
    ruoliAutorizzati: [],
    file: null
  });
  const [nodi, setNodi] = useState([]);
  const [ruoliDisponibili] = useState([
    { id: 'admin', nome: 'Amministratore' },
    { id: 'agenzia', nome: 'Agenzia' },
    { id: 'team_leader', nome: 'Team Leader' },
    { id: 'subagente', nome: 'Subagente' },
    { id: 'segnalatore', nome: 'Segnalatore' }
  ]);

  const categorie = [
    { 
      value: 'tutti', 
      label: 'Tutto', 
      icon: FolderIcon,
      gradient: 'from-gray-400 to-gray-500'
    },
    { 
      value: 'presentazione', 
      label: 'Presentazioni', 
      icon: PresentationChartLineIcon,
      gradient: 'from-purple-400 to-purple-600'
    },
    { 
      value: 'video', 
      label: 'Video', 
      icon: PlayIcon,
      gradient: 'from-red-400 to-red-600'
    },
    { 
      value: 'immagine', 
      label: 'Immagini', 
      icon: PhotoIcon,
      gradient: 'from-pink-400 to-pink-600'
    },
    { 
      value: 'altro', 
      label: 'Altro', 
      icon: FolderIcon,
      gradient: 'from-orange-400 to-orange-600'
    }
  ];

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    caricaMateriali();
    caricaNodi();
  }, [searchTerm, selectedCategoria]);

  const caricaMateriali = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategoria !== 'tutti' && { categoria: selectedCategoria })
      });

      const response = await fetch(`/api/marketing?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMateriali(data.materiali);
      } else {
        toast.error('Errore nel caricamento del materiale');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento del materiale');
    } finally {
      setLoading(false);
    }
  };

  const caricaNodi = async () => {
    try {
  // console.log('🔄 Caricamento nodi...');
      const response = await fetch('/api/marketing/utility/nodi', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

  // console.log('📡 Risposta nodi:', response.status);
      
      if (response.ok) {
        const data = await response.json();
  // console.log('📥 Nodi ricevuti:', data);
        setNodi(data.nodi || []);
  // console.log nodi caricati`);
      } else {
        console.error('❌ Errore risposta nodi:', response.status);
      }
    } catch (error) {
      console.error('❌ Errore nel caricamento nodi:', error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file || !uploadData.titolo.trim()) {
      toast.error('File e titolo sono obbligatori');
      return;
    }

    if (uploadData.tipoVisibilita === 'nodo' && !uploadData.nodoId) {
      toast.error('Seleziona un nodo per la visibilità per nodo');
      return;
    }

    if (uploadData.tipoVisibilita === 'ruolo' && uploadData.ruoliAutorizzati.length === 0) {
      toast.error('Seleziona almeno un ruolo per la visibilità per ruolo');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', uploadData.file);
      formData.append('titolo', uploadData.titolo);
      formData.append('descrizione', uploadData.descrizione);
      formData.append('categoria', uploadData.categoria);
      formData.append('tipoVisibilita', uploadData.tipoVisibilita);
      
      if (uploadData.tipoVisibilita === 'nodo' && uploadData.nodoId) {
        formData.append('nodoId', uploadData.nodoId);
      }
      
      if (uploadData.tipoVisibilita === 'ruolo' && uploadData.ruoliAutorizzati.length > 0) {
        formData.append('ruoliAutorizzati', JSON.stringify(uploadData.ruoliAutorizzati));
      }

      const response = await fetch('/api/marketing', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        toast.success('Materiale caricato con successo');
        setShowUploadModal(false);
        setUploadData({ titolo: '', descrizione: '', categoria: 'altro', tipoVisibilita: 'globale', nodoId: '', ruoliAutorizzati: [], file: null });
        caricaMateriali();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore nel caricamento');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel caricamento del materiale');
    }
  };

  const handleDownload = async (materiale) => {
    try {
      const response = await fetch(`/api/marketing/${materiale.id}/download`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = materiale.nomeFile;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        caricaMateriali();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore nel download');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel download del materiale');
    }
  };

  const handleDelete = async (materiale) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${materiale.titolo}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/marketing/${materiale.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Materiale eliminato con successo');
        caricaMateriali();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Errore nell\'eliminazione');
      }
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nell\'eliminazione del materiale');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCategoriaInfo = (categoria) => {
    return categorie.find(c => c.value === categoria) || categorie[0];
  };

  const getFilePreview = (materiale) => {
    const fileExtension = materiale.nomeFile?.split('.').pop()?.toLowerCase();
    const fileName = materiale.nomeFile || '';
    
    // Immagini
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(fileExtension)) {
      return {
        type: 'image',
        url: `/api/marketing/${materiale.id}/preview`,
        icon: PhotoIcon,
        color: 'from-pink-400 to-pink-600',
        displayName: 'Immagine'
      };
    }
    
    // Video
    if (['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(fileExtension)) {
      return {
        type: 'video',
        url: `/api/marketing/${materiale.id}/preview`,
        icon: PlayIcon,
        color: 'from-red-400 to-red-600',
        displayName: 'Video'
      };
    }
    
    // PDF
    if (fileExtension === 'pdf') {
      return {
        type: 'pdf',
        url: `/api/marketing/${materiale.id}/preview`,
        icon: DocumentIcon,
        color: 'from-red-500 to-red-600',
        displayName: 'PDF'
      };
    }
    
    // Presentazioni
    if (['ppt', 'pptx', 'key'].includes(fileExtension)) {
      return {
        type: 'presentation',
        icon: PresentationChartLineIcon,
        color: 'from-purple-400 to-purple-600',
        displayName: 'Presentazione'
      };
    }
    
    // Default
    return {
      type: 'file',
      icon: DocumentIcon,
      color: 'from-gray-400 to-gray-500',
      displayName: fileExtension?.toUpperCase() || 'FILE'
    };
  };

  const isAdmin = user?.role === 'admin';

  // Componente per l'anteprima dei file
  const PreviewComponent = ({ materiale, previewInfo, IconComponent }) => {
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    // Fallback generico
    const FallbackPreview = () => (
      <div className="w-full h-full flex flex-col items-center justify-center">
        <div className={`p-6 rounded-2xl bg-gradient-to-br ${previewInfo.color} text-white mb-3 shadow-lg`}>
          <IconComponent className="h-16 w-16" />
        </div>
        <span className="text-sm font-semibold text-gray-700">
          {previewInfo.displayName}
        </span>
      </div>
    );

    // Error - mostra fallback
    if (imageError) {
      return <FallbackPreview />;
    }

    if (previewInfo.type === 'image') {
      return (
        <div className="relative w-full h-full">
          {imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400"></div>
            </div>
          )}
          <img
            src={previewInfo.url}
            alt={materiale.titolo}
            className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110"
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageError(true);
              setImageLoading(false);
            }}
          />
        </div>
      );
    }

    if (previewInfo.type === 'video') {
      return (
        <div className="relative w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center overflow-hidden">
          <video 
            src={previewInfo.url} 
            className="absolute inset-0 w-full h-full object-cover opacity-30"
            muted
            loop
            playsInline
          />
          <div className="relative z-10 text-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-full p-6 mb-4 shadow-xl inline-flex">
              <PlayIcon className="h-12 w-12 text-red-600" />
            </div>
            <span className="text-white font-medium text-sm block">Video</span>
          </div>
        </div>
      );
    }

    if (previewInfo.type === 'pdf') {
      return (
        <div className="w-full h-full bg-gradient-to-br from-red-50 to-red-100 flex flex-col items-center justify-center">
          <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-6 rounded-2xl mb-3 shadow-lg">
            <DocumentIcon className="h-16 w-16" />
          </div>
          <span className="text-red-700 font-semibold text-sm">PDF Document</span>
        </div>
      );
    }

    return <FallbackPreview />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .preview-image {
          transition: transform 0.3s ease;
        }
        .group:hover .preview-image {
          transform: scale(1.05);
        }
        .video-overlay {
          background: linear-gradient(45deg, rgba(0,0,0,0.4), rgba(0,0,0,0.2));
        }
      `}</style>
      {/* Hero Section in stile Apple */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-light text-gray-900 mb-4">
              Marketing
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 font-light max-w-3xl mx-auto leading-relaxed">
              Tutto il materiale promozionale e commerciale.
              <br />
              Sempre aggiornato. Sempre disponibile.
            </p>
          </div>
        </div>
      </div>

      {/* Filtri Categoria in stile Apple */}
      <div className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-wrap justify-center gap-3">
            {categorie.map((categoria) => {
              const IconComponent = categoria.icon;
              const isSelected = selectedCategoria === categoria.value;
              
              return (
                <button
                  key={categoria.value}
                  onClick={() => setSelectedCategoria(categoria.value)}
                  className={`
                    group relative px-6 py-3 rounded-full font-medium transition-all duration-300 ease-out
                    ${isSelected 
                      ? 'bg-black text-white shadow-lg transform scale-105' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <IconComponent className="h-5 w-5" />
                    <span>{categoria.label}</span>
                  </div>
                  
                  {!isSelected && (
                    <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${categoria.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Barra di ricerca e azioni */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
          {/* Barra di ricerca */}
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca materiale..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white rounded-full border border-gray-200 focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>

          {/* Pulsante upload per admin */}
          {isAdmin && (
            <button
              onClick={() => setShowUploadModal(true)}
              className="bg-black text-white px-8 py-3 rounded-full font-medium hover:bg-gray-800 transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Carica Materiale</span>
            </button>
          )}
        </div>
      </div>

      {/* Grid dei materiali */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
          </div>
        ) : materiali.length === 0 ? (
          <div className="text-center py-20">
            <FolderIcon className="mx-auto h-24 w-24 text-gray-300 mb-4" />
            <h3 className="text-2xl font-light text-gray-900 mb-2">Nessun materiale trovato</h3>
            <p className="text-gray-500">
              {searchTerm || selectedCategoria !== 'tutti' 
                ? 'Prova a modificare i filtri di ricerca'
                : 'Non è ancora stato caricato alcun materiale'
              }
            </p>
          </div>
        ) : (
                     <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
             {materiali.map((materiale) => {
               const categoriaInfo = getCategoriaInfo(materiale.categoria);
               const previewInfo = getFilePreview(materiale);
               const IconComponent = previewInfo.icon;
               
               return (
                 <div
                   key={materiale.id}
                   className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-gray-200 transform hover:scale-105"
                 >
                   {/* Anteprima File */}
                   <div className="relative h-48 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
                     <PreviewComponent 
                       materiale={materiale} 
                       previewInfo={previewInfo} 
                       IconComponent={IconComponent}
                     />

                     {/* Badge categoria */}
                     <div className="absolute top-3 right-3">
                       <span className={`px-3 py-1 rounded-full text-xs font-medium text-white bg-gradient-to-r ${categoriaInfo.gradient} shadow-lg backdrop-blur-sm`}>
                         {categoriaInfo.label}
                       </span>
                     </div>

                     {/* Overlay informazioni file */}
                     <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                       <div className="flex items-center justify-between text-white text-xs">
                         <span className="font-medium">{previewInfo.displayName}</span>
                         <span className="opacity-75">{formatFileSize(materiale.dimensioneFile)}</span>
                       </div>
                     </div>
                   </div>

                   {/* Contenuto */}
                   <div className="p-6">
                     <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 text-lg">
                       {materiale.titolo}
                     </h3>
                     
                     {materiale.descrizione && (
                       <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                         {materiale.descrizione}
                       </p>
                     )}

                     <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                       <span className="flex items-center space-x-1">
                         <ArrowDownTrayIcon className="h-4 w-4" />
                         <span>{materiale.numeroDownload} download</span>
                       </span>
                       <span className="text-xs text-gray-400">
                         {new Date(materiale.createdAt).toLocaleDateString('it-IT')}
                       </span>
                     </div>

                     {/* Azioni */}
                     <div className="flex space-x-2">
                       <button
                         onClick={() => handleDownload(materiale)}
                         className="flex-1 bg-black text-white py-3 px-4 rounded-full text-sm font-medium hover:bg-gray-800 transition-all duration-200 flex items-center justify-center space-x-2 group"
                       >
                         <ArrowDownTrayIcon className="h-4 w-4 group-hover:animate-bounce" />
                         <span>Scarica</span>
                       </button>
                       
                       {isAdmin && (
                         <button
                           onClick={() => handleDelete(materiale)}
                           className="bg-red-50 text-red-600 p-3 rounded-full hover:bg-red-100 transition-all duration-200 group"
                         >
                           <TrashIcon className="h-4 w-4 group-hover:scale-110" />
                         </button>
                       )}
                     </div>
                   </div>
                 </div>
               );
             })}
          </div>
        )}
      </div>

      {/* Modal Upload in stile Apple */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-gray-900">
                  Carica Materiale
                </h2>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleUpload} className="p-6 space-y-6">
              {/* Upload file */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  File
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center hover:border-gray-300 transition-colors">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <div className="space-y-2">
                    <label className="cursor-pointer">
                      <span className="bg-black text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors">
                        Scegli file
                      </span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png,.mp4,.mov"
                      />
                    </label>
                    <p className="text-xs text-gray-500">
                      PDF, DOC, PPT, immagini o video
                    </p>
                    {uploadData.file && (
                      <p className="text-sm text-green-600 font-medium">
                        {uploadData.file.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Titolo */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Titolo
                </label>
                <input
                  type="text"
                  value={uploadData.titolo}
                  onChange={(e) => setUploadData({...uploadData, titolo: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none"
                  placeholder="Inserisci il titolo..."
                  required
                />
              </div>

              {/* Descrizione */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Descrizione
                </label>
                <textarea
                  value={uploadData.descrizione}
                  onChange={(e) => setUploadData({...uploadData, descrizione: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none resize-none"
                  placeholder="Descrizione opzionale..."
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Categoria
                </label>
                <select
                  value={uploadData.categoria}
                  onChange={(e) => setUploadData({...uploadData, categoria: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none"
                >
                  {categorie.slice(1).map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Visibilità */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Visibilità
                </label>
                <select
                  value={uploadData.tipoVisibilita}
                  onChange={(e) => setUploadData({...uploadData, tipoVisibilita: e.target.value, nodoId: '', ruoliAutorizzati: []})}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none"
                >
                  <option value="globale">Globale - Visibile a tutti</option>
                  <option value="nodo">Nodo specifico</option>
                  <option value="ruolo">Ruoli specifici</option>
                </select>
              </div>

              {/* Selezione Nodo (condizionale) */}
              {uploadData.tipoVisibilita === 'nodo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Seleziona Nodo
                  </label>
                  <select
                    value={uploadData.nodoId}
                    onChange={(e) => setUploadData({...uploadData, nodoId: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-black focus:ring-2 focus:ring-black focus:ring-opacity-10 transition-all duration-200 outline-none"
                    required
                  >
                    <option value="">Seleziona un nodo...</option>
                    {nodi.length === 0 && (
                      <option value="" disabled>Caricamento nodi...</option>
                    )}
                    {nodi.map((nodo) => (
                      <option key={nodo.id} value={nodo.id}>
                        {nodo.nome} ({nodo.tipoNodo})
                      </option>
                    ))}
                  </select>
                  {nodi.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      Caricamento nodi in corso...
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">
                    Debug: {nodi.length} nodi disponibili
                  </p>
                </div>
              )}

              {/* Selezione Ruoli (condizionale) */}
              {uploadData.tipoVisibilita === 'ruolo' && (
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-3">
                    Seleziona Ruoli Autorizzati
                  </label>
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3">
                    {ruoliDisponibili.map((ruolo) => (
                      <label key={ruolo.id} className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={uploadData.ruoliAutorizzati.includes(ruolo.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setUploadData({
                                ...uploadData,
                                ruoliAutorizzati: [...uploadData.ruoliAutorizzati, ruolo.id]
                              });
                            } else {
                              setUploadData({
                                ...uploadData,
                                ruoliAutorizzati: uploadData.ruoliAutorizzati.filter(r => r !== ruolo.id)
                              });
                            }
                          }}
                          className="rounded border-gray-300 text-black focus:ring-black focus:ring-opacity-50"
                        />
                        <span className="text-sm text-gray-700">{ruolo.nome}</span>
                      </label>
                    ))}
                  </div>
                  {uploadData.ruoliAutorizzati.length === 0 && (
                    <p className="text-sm text-red-600 mt-1">Seleziona almeno un ruolo</p>
                  )}
                </div>
              )}

              {/* Pulsanti */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 px-4 bg-black text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
                >
                  Carica
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingPage; 